/* eslint-disable no-use-before-define */
import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';

import {useDispatch, useSelector} from "react-redux";

import SearchIcon from '@mui/icons-material/Search';

import useDataSource from "../../../hooks/useDataSource";
import {dataAddFilter, dataRemoveFilter, dataSetSelectedProjectVals, filterUpdateConditionAction} from "../../../redux/data/dataActions";

import { styled, Autocomplete, TextField, Popper, Chip, alpha } from '@mui/material';

const PREFIX = 'SearchBox';

const classes = {
    search: `${PREFIX}-search`,
    searchIcon: `${PREFIX}-searchIcon`,
    autoComplete: `${PREFIX}-autoComplete`,
    textField: `${PREFIX}-textField`,
    inputRoot: `${PREFIX}-inputRoot`,
    inputInput: `${PREFIX}-inputInput`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.search}`]: {
		display: 'inline-flex',
		position: 'relative',
		float: 'right',
		borderRadius: '4px',
		backgroundColor: alpha(theme.palette.common.white, 0.15),
		'&:hover': {
			backgroundColor: alpha(theme.palette.common.white, 0.25),
		},
		[theme.breakpoints.up('sm')]: {
			marginLeft: theme.spacing(1),
			width: '100%',
		}
	},

    [`& .${classes.searchIcon}`]: {
		width: '72px',
		height: 'auto',
		display: 'flex',
		alignItems: 'center',
		pointerEvents: 'none',
		justifyContent: 'center',
	},

    [`& .${classes.autoComplete}`]: {
		width: '100%',
		minWidth: '150px'
	},

    [`& .${classes.textField}`]: {
		paddingTop: '4px',
		paddingRight: '0px',
		paddingBottom: '2px'
	},

    [`& .${classes.inputRoot}`]: {
		color: 'inherit',
		overflowY: 'auto',
		maxHeight: '58px',
		overflowX: 'hidden',
	},

    [`& .${classes.inputInput}`]: {
		// paddingLeft: `calc(1em + ${theme.spacing(4)})`,
		transition: theme.transitions.create('width'),
		width: '100%',
		[theme.breakpoints.up('sm')]: {
			'&:focus': {
				width: '30ch !important'
			},
		},
	}
}));

export default function SearchBox({searchConfig}) {
	const dispatch = useDispatch();

	const { t } = useTranslation();
	const searchHint = "Projects";

	// App State
	const dataSource = useDataSource(searchConfig[0].options.dataSourceName);
	const filters = useSelector(state => state.data.filters);
	const defaultOptions = useSelector(state => state.data.dataSelectedProjectsVals);
	const filterID = dataSource.name.toLowerCase() + '-' + searchConfig[0].options.valueField.toLowerCase();

	// Component State
	const [options, setOptions] = useState([]);
	const [selectedOptions, setSelectedOptions] = useState(defaultOptions);

	// https://github.com/mui-org/material-ui/issues/19376
	const myPopper = function (props) {
		return (<Popper {...props} style={{ width: '350px' }} placement={"bottom-end"} />)
	}

	useEffect(() => {
		const componentFilters = filters.find(f => {
			return f.id === filterID
		});

		if (!componentFilters) {
			setSelectedOptions([]); // No filters?  Clear selected options!
			dispatch(dataSetSelectedProjectVals([])); // No filters?  Clear persisted selected options!
		}
	},[filters])

	useEffect(() => {

		// console.log('-----------------SEARCH BOX---------STATUS->');
		// console.log('defaultOptions:', defaultOptions);
		// console.log('filters:', filters);
		// console.log('fetching: {0}'.format(dataSource.fetching));
		// console.log('fetched: {0}'.format(dataSource.fetched));
		// console.log('error: {0}'.format(dataSource.error));
		// console.log('dataSource:', dataSource);
		// console.log('dataSource Name:', dataSource.name);
		// console.log('data:', dataSource.data);
		// console.log('baselines:', dataSource.baselines);

		if (dataSource.data && dataSource.fetched) {
			// Setup base data as filtered results or a baseline if included
			let data = dataSource.data;
			if (dataSource.includeBaselineResults && dataSource.baselines) {
				if (dataSource.baselines.length > 0) {
					// Find my baseline
					const baseline = dataSource.baselines.find(d => {
						return d.id === filterID
					});

					// console.log('------------BASELINE------------->', baseline);
					if (baseline) {
						let useBaseline = true;

						if (useBaseline) {
							data = baseline.result;
						}
					}
				}
			}

			// Only update options when data is filtered or page change
			// If lengths different => crossfilter on same page || OR || If options length === 0 AND no selected options on page change
			if ((options.length !== data.length) || ((options.length === 0) && (defaultOptions === []))) {
				// This is to allow Autocomplete to not be generated with new data and to filter selected options
				// console.log('------------SET DATA-------------');
				// console.log('options.length: ', options.length);
				// console.log('data.length: ', data.length);
				// console.log('defaultOptions: ', defaultOptions);

				// If a Page change (lengths not equal) and selected options exist, remove from available drop down options
				if (defaultOptions.length > 0){
					data = data.filter(el => defaultOptions.find(rm => (rm.id === el.id)));
				}

				// Set Options
				setOptions(data);
			}
		}
	}, [dataSource.data]);

	return (
        <Root className={classes.search}>
			<div className={classes.searchIcon}>
				<SearchIcon/>
			</div>
			<Autocomplete
				multiple
				id="size-small-filled-multi"
				className={classes.autoComplete}
				size="small"
				limitTags={1}
				autoHighlight={true}
				options={options ? options : {title: "No Data", value: "No Value"}}
				getOptionLabel={(option) => option[searchConfig[0].options.textField]}
				//defaultValue={defaultOptions}
				value={selectedOptions}
				filterSelectedOptions={true}
				PopperComponent={myPopper}
				renderTags={(value, getTagProps) =>
					value.map((option, index) => (
						<Chip
							// variant="outlined"
							// color="secondary"
							label={t(option[searchConfig[0].options.textField])}
							size="small"
							{...getTagProps({ index })}
						/>
					))
				}
				renderInput={(params) => (
					<TextField {...params}
							   placeholder={t(searchHint)}
							   className={classes.textField}
							   size="small"
							   variant="standard"
							   InputProps={{...params.InputProps,
								   			disableUnderline: true,
								   			classes: {
												root: classes.inputRoot,
												input: classes.inputInput
											}
							   }}
					/>
				)}
				// renderOption={(props, option, { selected }) => {
				// 	const matches = match(option[searchConfig[0].options.textField], selected);
				// 	const parts = parse(option[searchConfig[0].options.textField], matches);
				//
				// 	return (parts.map((part, index) => (
				// 				<li key={index} sx={{ fontWeight: part.highlight ? 700 : 400 }}>
                // 					{part.text}
              	// 				</li>
				// 			))
				// 	);
				// }}
				onChange={(event, value) => {
						if (value.length > 0) {
							dispatch(dataSetSelectedProjectVals(value))
							dispatch(dataAddFilter(filterID, searchConfig[0].options.valueField, " INN ({0})".format(value.map(function(val){return val[searchConfig[0].options.valueField];}).join(",")), filterUpdateConditionAction.REPLACE, true, false))
							setSelectedOptions(value);
						} else {
							dispatch(dataSetSelectedProjectVals([]))
							dispatch(dataRemoveFilter(filterID))
							setSelectedOptions([]);
						}
					}
				}
			/>
		</Root>
    );
}
